import { z } from "zod";
import { AccountIdSchema } from "./identity.ts";
import { IdSchema, TimestampSchema, WpIdSchema } from "./primitives.ts";

/**
 * Lifecycle of an agent session.
 * - `active`: heartbeating within the idle threshold.
 * - `idle`: registered but no heartbeat for a while; claims still held.
 * - `landed`: the session's work was accepted and merged; its claims are released.
 * - `expired`: no heartbeat past the session TTL; its claims are released.
 *
 * `landed` and `expired` are terminal.
 */
export const SessionStatusSchema = z.enum(["active", "idle", "landed", "expired"]);
/** See {@link SessionStatusSchema}. */
export type SessionStatus = z.infer<typeof SessionStatusSchema>;

/**
 * One running agent working on one work package in its own fork.
 * Everything an agent does through the coordinator (claims, messages, diffs) is attributed to a session.
 */
export const SessionSchema = z.object({
  /** Coordinator-assigned id. Pass it on every later call. */
  id: IdSchema,
  /** Human-readable agent name, for example `contracts-agent` or `alpha`. */
  agentName: z.string().min(1).max(64),
  /** The work package this session is delivering. */
  wpId: WpIdSchema,
  /** The fork the agent pushes to, as `<owner>/<repo>` or an Artifacts repo name, for example `bots/botallion-wp03`. */
  forkRepo: z.string().min(1),
  /** Git remote URL of the fork, for example `https://github.com/bots/botallion-wp03.git`. */
  forkRemote: z.string().min(1),
  /** Current lifecycle state, see {@link SessionStatusSchema}. */
  status: SessionStatusSchema,
  /** When the session registered. */
  startedAt: TimestampSchema,
  /** When the coordinator last heard from the session. Drives `idle` and `expired`. */
  lastHeartbeat: TimestampSchema,
  /** The agent's latest progress note (sent with a heartbeat), shown on the dashboard. Null until one is sent. */
  note: z.string().max(500).nullable(),
  /**
   * The coding agent product the session runs on, as its dispatcher reports it, for example `Claude Code`,
   * `Codex CLI` or `Gemini CLI`. Display only: nothing in the coordinator or the protocol reads it. Absent when
   * the dispatcher did not say.
   */
  vendor: z.string().min(1).max(64).optional(),
  /**
   * contracts-v2: the account that issued the seat this session runs on (`acc_...`), so every agent action traces
   * back to one account. Absent for sessions registered before accounts existed.
   */
  accountId: AccountIdSchema.optional(),
});
/** See {@link SessionSchema}. */
export type Session = z.infer<typeof SessionSchema>;
